/**
 * Human-readable formatting for AI Notetaker timing events (local dev only).
 * Keep in sync with medplum-server/src/lib/timing/aiNotetakerTimingFormat.ts
 */

export type TimingEventLike = {
  traceId: string;
  phase: string;
  event: string;
  timestamp: string;
  durationMs?: number;
  status?: 'started' | 'finished' | 'failed' | string;
  appointmentId?: string;
  encounterId?: string;
  questionnaireResponseId?: string;
  zoomMeetingId?: string;
  aiRunId?: string;
  model?: string;
  transcriptChars?: number;
  inputTokens?: number;
  outputTokens?: number;
  errorMessage?: string;
  reason?: string;
};

export const PHASE_LABELS: Record<string, string> = {
  zoom_transcript: 'Zoom Transcript',
  ai_generation: 'AI Generation',
};

export const EVENT_LABELS: Record<string, string> = {
  zoom_webhook_received_at: 'Webhook received',
  zoom_webhook_validated_at: 'Signature validated',
  zoom_meeting_ended_at: 'Meeting ended (Zoom)',
  zoom_recording_event_received_at: 'Recording event queued',
  zoom_webhook_processed_at: 'Webhook forwarded to bots',
  zoom_webhook_forward_failed: 'Webhook forward failed',
  zoom_bot_execute_started_at: 'Bot $execute started',
  zoom_bot_execute_finished_at: 'Bot $execute finished',
  zoom_bot_handler_started_at: 'Bot handler started',
  zoom_auth_failed: 'Zoom auth failed',
  appointment_search_failed: 'Appointment search failed',
  appointment_resolved_at: 'Appointment resolved',
  transcript_download_started_at: 'Transcript download started',
  transcript_download_finished_at: 'Transcript downloaded',
  transcript_not_available: 'Transcript not in Zoom',
  transcript_mark_ready_started_at: 'Marking transcript ready',
  transcript_mark_ready_finished_at: 'Transcript marked ready',
  transcript_mark_ready_failed: 'Mark transcript ready failed',
  appointment_poll_transcript_started_at: 'Polling for transcript ready',
  appointment_ui_sees_transcript_ready_at: 'UI sees transcript ready',
  appointment_transcript_not_ready_after_poll: 'Transcript not ready after poll',
  ai_generate_clicked_at: 'User clicked Generate',
  ai_generate_request_prepared_at: 'Request prepared',
  ai_generate_request_sent_at: 'Bot $execute sent',
  ai_generate_response_returned_at: 'Bot $execute returned',
  ai_generate_request_failed: 'Bot $execute failed',
  ai_generate_failed: 'Generation failed (UI)',
  ai_bot_started_at: 'Bot handler started',
  ai_context_fetch_started_at: 'Fetching context + transcript',
  ai_transcript_fetched_at: 'Transcript fetched from Zoom',
  ai_context_fetch_finished_at: 'Context assembly finished',
  ai_context_fetch_failed: 'Context fetch failed',
  ai_model_request_started_at: 'LLM request started',
  ai_model_response_received_at: 'LLM response received',
  ai_parse_validate_started_at: 'Parsing + validating note',
  ai_parse_validate_finished_at: 'Parse + validate finished',
  ai_qr_update_started_at: 'Saving QuestionnaireResponse',
  ai_qr_update_finished_at: 'QuestionnaireResponse saved',
  ai_generation_error: 'Generation error',
  ai_note_poll_started_at: 'Polling for draft note',
  ai_note_rendered_at: 'Draft note available',
  ai_generation_failed_rendered_at: 'Generation failed (visible in UI)',
  ai_note_poll_timeout: 'Draft poll timed out',
  ai_note_poll_skipped: 'Draft poll skipped',
};

type SegmentDef = { key: string; label: string; start: string; end: string };

const AI_SEGMENTS: SegmentDef[] = [
  { key: 'user_perceived', label: 'User perceived (click → note)', start: 'ai_generate_clicked_at', end: 'ai_note_rendered_at' },
  { key: 'execute', label: 'Bot $execute round-trip', start: 'ai_generate_request_sent_at', end: 'ai_generate_response_returned_at' },
  { key: 'poll_draft', label: 'Poll until draft', start: 'ai_note_poll_started_at', end: 'ai_note_rendered_at' },
  { key: 'bot_startup', label: 'Bot cold start', start: 'ai_generate_request_sent_at', end: 'ai_bot_started_at' },
  { key: 'context', label: 'Context + transcript fetch', start: 'ai_context_fetch_started_at', end: 'ai_context_fetch_finished_at' },
  { key: 'model', label: 'LLM latency', start: 'ai_model_request_started_at', end: 'ai_model_response_received_at' },
  { key: 'parse', label: 'Parse + validate', start: 'ai_parse_validate_started_at', end: 'ai_parse_validate_finished_at' },
  { key: 'persist', label: 'Save to FHIR', start: 'ai_qr_update_started_at', end: 'ai_qr_update_finished_at' },
  { key: 'ui_render', label: 'UI render after $execute', start: 'ai_generate_response_returned_at', end: 'ai_note_rendered_at' },
];

const ZOOM_SEGMENTS: SegmentDef[] = [
  { key: 'forward', label: 'Webhook → bot forward', start: 'zoom_recording_event_received_at', end: 'zoom_webhook_processed_at' },
  { key: 'execute', label: 'Bot $execute round-trip', start: 'zoom_bot_execute_started_at', end: 'zoom_bot_execute_finished_at' },
  { key: 'download', label: 'Transcript download', start: 'transcript_download_started_at', end: 'transcript_download_finished_at' },
  { key: 'mark_ready', label: 'Mark transcript ready', start: 'transcript_mark_ready_started_at', end: 'transcript_mark_ready_finished_at' },
  { key: 'poll', label: 'Poll until UI ready', start: 'appointment_poll_transcript_started_at', end: 'appointment_ui_sees_transcript_ready_at' },
];

export function roundDurationMs(ms: number): number {
  return Math.round(ms * 10) / 10;
}

export function formatDuration(ms: number | undefined | null): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const rounded = Math.round(ms);
  if (rounded < 1000) return `${rounded}ms`;
  if (rounded < 60_000) return `${(rounded / 1000).toFixed(1)}s`;
  const mins = Math.floor(rounded / 60_000);
  const secs = Math.round((rounded % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

export function formatEventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/_at$/g, '').replace(/_/g, ' ');
}

export function formatPhaseLabel(phase: string): string {
  return PHASE_LABELS[phase] ?? phase;
}

export function shortTraceId(traceId: string): string {
  const parts = traceId.split(':');
  const runId = parts[parts.length - 1] ?? traceId;
  const appt = parts[2];
  if (appt && appt !== 'unknown') {
    return `${appt.slice(0, 8)}…/${runId}`;
  }
  return runId;
}

function eventNotes(event: TimingEventLike): string | undefined {
  const parts: string[] = [];
  if (event.model) parts.push(event.model);
  if (event.transcriptChars != null) parts.push(`${event.transcriptChars.toLocaleString()} chars`);
  if (event.inputTokens != null) parts.push(`in ${event.inputTokens}`);
  if (event.outputTokens != null) parts.push(`out ${event.outputTokens}`);
  if (event.questionnaireResponseId) parts.push(`QR ${event.questionnaireResponseId.slice(0, 8)}…`);
  if (event.reason) parts.push(String(event.reason));
  if (event.errorMessage) parts.push(event.errorMessage.slice(0, 80));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export function formatConsoleLine(event: TimingEventLike): string {
  const phase = formatPhaseLabel(event.phase);
  const label = formatEventLabel(event.event);
  const dur = event.durationMs != null ? ` +${formatDuration(event.durationMs)}` : '';
  const status =
    event.status === 'failed' ? ' ✗' : event.status === 'started' ? ' …' : '';
  const notes = eventNotes(event);
  const noteSuffix = notes ? ` | ${notes}` : '';
  const ids: string[] = [];
  if (event.appointmentId) ids.push(`appt ${event.appointmentId.slice(0, 8)}…`);
  if (event.zoomMeetingId) ids.push(`zoom ${event.zoomMeetingId}`);
  const idSuffix = ids.length > 0 ? ` | ${ids.join(' · ')}` : '';
  return `[AI_TIMING] ${phase} › ${label}${dur}${status}${idSuffix}${noteSuffix} | ${shortTraceId(event.traceId)}`;
}

export type TraceStep = {
  event: string;
  label: string;
  timestamp: string;
  durationMs?: number;
  status?: string;
  notes?: string;
};

export type TraceSegment = {
  key: string;
  label: string;
  ms?: number;
};

export type TraceOutcome = 'success' | 'failed' | 'timeout' | 'skipped' | 'in_progress' | 'unknown';

export type TraceSummary = {
  traceId: string;
  shortId: string;
  phase: string;
  phaseLabel: string;
  appointmentId?: string;
  zoomMeetingId?: string;
  aiRunId?: string;
  outcome: TraceOutcome;
  outcomeLabel: string;
  totalMs?: number;
  startedAt?: string;
  finishedAt?: string;
  steps: TraceStep[];
  segments: TraceSegment[];
  errorMessage?: string;
};

function eventTimeMs(event: TimingEventLike): number {
  return new Date(event.timestamp).getTime();
}

function segmentMs(events: TimingEventLike[], start: string, end: string): number | undefined {
  const startEvt = events.find((e) => e.event === start);
  const endEvt = events.find((e) => e.event === end);
  if (!startEvt || !endEvt) return undefined;
  const ms = eventTimeMs(endEvt) - eventTimeMs(startEvt);
  return ms >= 0 ? roundDurationMs(ms) : undefined;
}

function inferOutcome(events: TimingEventLike[]): { outcome: TraceOutcome; label: string } {
  const last = events[events.length - 1];
  if (events.some((e) => e.status === 'failed' || e.event.includes('failed'))) {
    return { outcome: 'failed', label: 'Failed' };
  }
  if (events.some((e) => e.event === 'ai_note_poll_timeout')) {
    return { outcome: 'timeout', label: 'Timed out' };
  }
  if (events.some((e) => e.event === 'ai_note_poll_skipped')) {
    return { outcome: 'skipped', label: 'Skipped poll' };
  }
  if (events.some((e) => e.event === 'ai_generation_failed_rendered_at')) {
    return { outcome: 'failed', label: 'Generation failed' };
  }
  const rendered = events.find((e) => e.event === 'ai_note_rendered_at');
  if (rendered?.status === 'generation-failed') {
    return { outcome: 'failed', label: 'Generation failed' };
  }
  if (
    events.some(
      (e) =>
        e.event === 'ai_note_rendered_at' ||
        e.event === 'appointment_ui_sees_transcript_ready_at' ||
        e.event === 'zoom_webhook_processed_at',
    )
  ) {
    return { outcome: 'success', label: 'Complete' };
  }
  if (last?.status === 'started') {
    return { outcome: 'in_progress', label: 'In progress' };
  }
  return { outcome: 'unknown', label: 'Unknown' };
}

export function buildTraceSummary(traceId: string, events: TimingEventLike[]): TraceSummary {
  const sorted = [...events].sort((a, b) => eventTimeMs(a) - eventTimeMs(b));
  const phase = sorted[0]?.phase ?? 'unknown';
  const segmentDefs = phase === 'ai_generation' ? AI_SEGMENTS : ZOOM_SEGMENTS;

  const steps: TraceStep[] = sorted.map((e) => ({
    event: e.event,
    label: formatEventLabel(e.event),
    timestamp: e.timestamp,
    durationMs: e.durationMs != null ? roundDurationMs(e.durationMs) : undefined,
    status: e.status,
    notes: eventNotes(e),
  }));

  const segments: TraceSegment[] = [];
  for (const def of segmentDefs) {
    const ms = segmentMs(sorted, def.start, def.end);
    if (ms != null) segments.push({ key: def.key, label: def.label, ms });
  }

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalMs =
    first && last ? roundDurationMs(eventTimeMs(last) - eventTimeMs(first)) : undefined;
  const { outcome, label: outcomeLabel } = inferOutcome(sorted);
  const errorEvent = [...sorted].reverse().find((e) => e.errorMessage || e.status === 'failed');

  return {
    traceId,
    shortId: shortTraceId(traceId),
    phase,
    phaseLabel: formatPhaseLabel(phase),
    appointmentId: sorted.find((e) => e.appointmentId)?.appointmentId,
    zoomMeetingId: sorted.find((e) => e.zoomMeetingId)?.zoomMeetingId,
    aiRunId: sorted.find((e) => e.aiRunId)?.aiRunId,
    outcome,
    outcomeLabel,
    totalMs,
    startedAt: first?.timestamp,
    finishedAt: last?.timestamp,
    steps,
    segments,
    errorMessage: errorEvent?.errorMessage,
  };
}

export function groupEventsByTrace<T extends TimingEventLike>(events: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const e of events) {
    const list = map.get(e.traceId) ?? [];
    list.push(e);
    map.set(e.traceId, list);
  }
  return map;
}

export function buildTraceSummaries<T extends TimingEventLike>(events: T[]): TraceSummary[] {
  const groups = groupEventsByTrace(events);
  return Array.from(groups.entries())
    .map(([traceId, evts]) => buildTraceSummary(traceId, evts))
    .sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''));
}

export function formatTraceSummaryText(summary: TraceSummary): string {
  const lines: string[] = [];
  lines.push(`${summary.phaseLabel} · ${summary.outcomeLabel} · ${formatDuration(summary.totalMs)}`);
  lines.push(`  trace ${summary.shortId}`);
  if (summary.appointmentId) lines.push(`  appointment ${summary.appointmentId}`);
  if (summary.zoomMeetingId) lines.push(`  zoom meeting ${summary.zoomMeetingId}`);
  if (summary.errorMessage) lines.push(`  error: ${summary.errorMessage}`);

  if (summary.segments.length > 0) {
    lines.push('  segments:');
    for (const seg of summary.segments) {
      lines.push(`    ${seg.label.padEnd(32)} ${formatDuration(seg.ms).padStart(8)}`);
    }
  }

  lines.push('  timeline:');
  for (const step of summary.steps) {
    const dur = step.durationMs != null ? ` +${formatDuration(step.durationMs)}` : '';
    const status = step.status === 'failed' ? ' ✗' : step.status === 'started' ? ' …' : '';
    const notes = step.notes ? ` (${step.notes})` : '';
    const time = new Date(step.timestamp).toISOString().slice(11, 23);
    lines.push(`    ${time}  ${step.label}${dur}${status}${notes}`);
  }

  return lines.join('\n');
}
